import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { ArrowUpDown, ChevronDown, ChevronUp, Search } from "lucide-react";
import Cookies from "js-cookie";
import storage from "utils/Storage";
import { API } from "utils/Helper";
import { formatUserDate } from "utils/timezoneHelper";

const Container = styled.div`
  color: #f5f6f7;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
`;

const PageDesc = styled.p`
  margin-top: 8px;
  color: #b1b6bb;
  font-size: 14px;
  line-height: 1.6;
`;

const CampaignSelectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;

  .label {
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    min-width: 80px;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 280px;
  max-width: 440px;
`;

const DropdownTrigger = styled.button`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  
  .text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.5);
  z-index: 40;
  overflow: hidden;

  .search-box {
    padding: 8px;
    border-bottom: 1px solid rgba(58, 65, 66, 0.3);
    
    .input-wrap {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 36px;
      padding: 0 12px;
      background: #1a1d1e;
      border: 1px solid #3a4142;
      border-radius: 4px;
      
      input {
        background: transparent;
        border: none;
        color: #ffffff;
        font-size: 13px;
        outline: none;
        width: 100%;
      }
    }
  }

  .options-wrap {
    max-height: 240px;
    overflow-y: auto;
    
    button {
      width: 100%;
      text-align: left;
      padding: 12px 16px;
      background: transparent;
      border: none;
      color: #f5f6f7;
      font-size: 14px;
      cursor: pointer;
      transition: background 0.2s;

      &:hover {
        background: rgba(58, 65, 66, 0.4);
      }

      &.active {
        background: rgba(31, 193, 107, 0.15);
        font-weight: bold;
      }
    }
  }
`;

const SortContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
  
  .sort-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
  }
`;

const SortDropdownTrigger = styled.button`
  width: 100%;
  height: 48px;
  padding: 0 16px;
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  margin-top: 8px;
`;

const SortDropdownMenu = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 6px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.5);
  overflow: hidden;
  z-index: 40;

  button {
    width: 100%;
    text-align: left;
    padding: 12px 16px;
    background: transparent;
    border: none;
    color: #f5f6f7;
    font-size: 14px;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: rgba(58, 65, 66, 0.4);
    }

    &.active {
      background: rgba(31, 193, 107, 0.15);
      font-weight: bold;
    }
  }
`;

const TableContainer = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 20px;
  
  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 680px;
    
    th, td {
      padding: 16px 20px;
      text-align: left;
      font-size: 14px;
    }
    
    th {
      color: #b1b6bb;
      font-weight: 600;
      border-bottom: 1px solid rgba(58, 65, 66, 0.3);
    }
    
    td {
      color: #ffffff;
      border-bottom: 1px solid rgba(58, 65, 66, 0.2);
    }
    
    tr:last-child td {
      border-bottom: none;
    }
  }
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid rgba(58, 65, 66, 0.3);
  background: #1a1d1e;
  font-size: 13px;
  color: #b1b6bb;

  .buttons {
    display: flex;
    align-items: center;
    gap: 6px;
    
    button.nav {
      height: 32px;
      padding: 0 12px;
      background: #292d2e;
      border: 1px solid #3a4142;
      border-radius: 6px;
      color: #ffffff;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
      &:not(:disabled):hover {
        background: rgba(58, 65, 66, 0.4);
      }
    }

    button.num {
      width: 32px;
      height: 32px;
      border-radius: 6px;
      background: #292d2e;
      border: 1px solid #3a4142;
      color: #ffffff;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.2s;
      
      &:hover {
        background: rgba(58, 65, 66, 0.4);
      }

      &.active {
        background: #1fc16b;
        border-color: #1fc16b;
        color: #ffffff;
      }
    }
  }
`;

const SORT_OPTIONS = {
  "created_at_desc": "Registered: New to Old",
  "created_at_asc": "Registered: Old to New",
  "total_deposits_desc": "Total Deposits: High to Low",
  "total_deposits_asc": "Total Deposits: Low to High",
  "commission_desc": "Overall Commission: High to Low",
  "commission_asc": "Overall Commission: Low to High"
};

const getUserId = () => {
  let uid = Cookies.get("uid");
  if (!uid) {
    try {
      const token = storage.getKey("token");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        uid = payload.id || payload.uid;
      }
    } catch (e) {}
  }
  return uid ? Number(uid) : null;
};

const ReferredUsers = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [campaignSearchQuery, setCampaignSearchQuery] = useState("");
  const [campaignOpen, setCampaignOpen] = useState(false);

  const [sortOpen, setSortOpen] = useState(false);
  const [sortKey, setSortKey] = useState("created_at_desc");

  const [page, setPage] = useState(1);
  const LIMIT = 10;
  const uid = getUserId();

  // Load campaigns list for filter dropdown
  useEffect(() => {
    if (!uid) return;
    fetch(`${API}/api/affiliate/campaigns/${uid}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setCampaigns(data.campaigns);
        }
      })
      .catch(err => console.error("Error fetching filter campaigns:", err));
  }, [uid]);

  // Load referred users with parameters
  const fetchReferredUsers = async () => {
    if (!uid) return;
    try {
      setLoading(true);
      const res = await fetch(
        `${API}/api/affiliate/referred-users/${uid}?campaignId=${selectedCampaignId}&sortKey=${sortKey}&page=${page}&limit=${LIMIT}`
      );
      const data = await res.json();
      if (data.success) {
        setUsersList(data.data);
        setTotalCount(data.total);
      }
    } catch (err) {
      console.error("Error fetching referred users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferredUsers();
  }, [uid, selectedCampaignId, sortKey, page]);

  const campaignNameSelected = selectedCampaignId 
    ? campaigns.find(c => c.id === selectedCampaignId)?.name || "Unknown Campaign"
    : "All Campaigns";

  const filteredCampaigns = campaigns.filter(c => 
    (c.name && c.name.toLowerCase().includes(campaignSearchQuery.toLowerCase())) || 
    (c.code && c.code.toLowerCase().includes(campaignSearchQuery.toLowerCase()))
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / LIMIT));

  return (
    <Container>
      <PageTitle>Referred Users</PageTitle>
      <PageDesc>
        Track all the users who joined through your referral link. Here you can see their activity and gameplay.
      </PageDesc>

      <CampaignSelectorRow>
        <div className="label">Campaign</div>
        <DropdownContainer>
          <DropdownTrigger onClick={() => setCampaignOpen(!campaignOpen)}>
            <span className="text">{campaignNameSelected}</span>
            <ChevronDown size={16} />
          </DropdownTrigger>
          {campaignOpen && (
            <DropdownMenu>
              <div className="search-box">
                <div className="input-wrap">
                  <Search size={14} style={{ color: "#b1b6bb" }} />
                  <input 
                    type="text" 
                    placeholder="Search Campaign"
                    value={campaignSearchQuery}
                    onChange={(e) => setCampaignSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="options-wrap">
                <button 
                  className={!selectedCampaignId ? "active" : ""}
                  onClick={() => {
                    setSelectedCampaignId("");
                    setCampaignOpen(false);
                    setCampaignSearchQuery("");
                    setPage(1);
                  }}
                >
                  All Campaigns
                </button>
                {filteredCampaigns.map(c => (
                  <button 
                    key={c.id}
                    className={selectedCampaignId === c.id ? "active" : ""}
                    onClick={() => {
                      setSelectedCampaignId(c.id);
                      setCampaignOpen(false);
                      setCampaignSearchQuery("");
                      setPage(1);
                    }}
                  >
                    {c.name} ({c.code})
                  </button>
                ))}
              </div>
            </DropdownMenu>
          )}
        </DropdownContainer>
      </CampaignSelectorRow>

      <SortContainer>
        <div className="sort-label">
          <ArrowUpDown size={14} />
          <span>Sort</span>
        </div>
        <SortDropdownTrigger onClick={() => setSortOpen(!sortOpen)}>
          <span>{SORT_OPTIONS[sortKey]}</span>
          <ChevronDown size={16} />
        </SortDropdownTrigger>
        {sortOpen && (
          <SortDropdownMenu>
            {Object.keys(SORT_OPTIONS).map(k => (
              <button 
                key={k}
                className={sortKey === k ? "active" : ""}
                onClick={() => {
                  setSortKey(k);
                  setSortOpen(false);
                  setPage(1);
                }}
              >
                {SORT_OPTIONS[k]}
              </button>
            ))}
          </SortDropdownMenu>
        )}
      </SortContainer>

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#b1b6bb" }}>Loading referred users...</div>
      ) : (
        <TableContainer>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Campaign</th>
                  <th>Registered</th>
                  <th style={{ textAlign: "right" }}>Total Deposits</th>
                  <th style={{ textAlign: "right" }}>Wagered</th>
                  <th style={{ textAlign: "right" }}>Commission</th>
                </tr>
              </thead>
              <tbody>
                {usersList.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", color: "#b1b6bb", fontStyle: "italic", padding: "30px" }}>No referred users found.</td>
                  </tr>
                ) : usersList.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: "700" }}>{u.username}</td>
                    <td style={{ color: "#b1b6bb" }}>{u.campaign_name}</td>
                    <td style={{ color: "#b1b6bb" }}>{formatUserDate(u.registered)}</td>
                    <td style={{ fontFamily: "monospace", textAlign: "right" }}>${parseFloat(u.total_deposits).toFixed(2)}</td>
                    <td style={{ fontFamily: "monospace", textAlign: "right" }}>${parseFloat(u.wagered).toFixed(2)}</td>
                    <td style={{ fontFamily: "monospace", textAlign: "right", color: "#1fc16b", fontWeight: "700" }}>${parseFloat(u.commission).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PaginationRow>
            <div>
              {totalCount > 0 ? `Showing ${(page - 1) * LIMIT + 1}–${Math.min(page * LIMIT, totalCount)} of ${totalCount}` : "No results"}
            </div>
            <div className="buttons">
              <button className="nav" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button 
                  key={i} 
                  className={`num ${page === i + 1 ? "active" : ""}`}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button className="nav" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          </PaginationRow>
        </TableContainer>
      )}
    </Container>
  );
};

export default ReferredUsers;
